import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Wrapper from "../../components/Wrapper";
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import IconButton from "@mui/material/IconButton";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Api from "../../Api";
import { telecomunication } from "../../translate";

export default function WhatsAppOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const langIndex = +localStorage.getItem("atsLang") || 1;
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const createAttemptedRef = useRef(false);

  useEffect(() => {
    if (createAttemptedRef.current) return;

    const ok = searchParams.get("ok");
    const err = searchParams.get("error");
    const reqId = searchParams.get("reqId"); // <-- oauth_state from backend
    const phone_number_id = searchParams.get("phone_number_id");
    const wa_phone_number = searchParams.get("wa_phone_number");
    const wa_title = searchParams.get("wa_title") || null;
    const sip_username = searchParams.get("sip_username") || null;

    if (err) {
      createAttemptedRef.current = true;
      setError(
        err +
          (searchParams.get("error_description")
            ? `: ${searchParams.get("error_description")}`
            : "")
      );
      setStatus("error");
      return;
    }

    if (ok === "1" && reqId && phone_number_id && wa_phone_number) {
      createAttemptedRef.current = true;

      const doCreate = async () => {
        try {
          // ✅ Finalize using oauth_state only (password is already saved on backend in wa_phone)
          await Api.whatsappCreate({
            oauth_state: reqId,
          });

          setStatus("success");
          setTimeout(() => navigate("/connect/whatsapp-number", { replace: true }), 1500);
        } catch (e) {
          setError(e?.response?.data?.error || e?.message || "Failed to save");
          setStatus("error");
        }
      };

      doCreate();
      return;
    }

    setStatus("error");
    setError(searchParams.get("error") || "Missing callback data");
    createAttemptedRef.current = true;
  }, [searchParams, navigate]);

  const goToNumbers = () => navigate("/connect/whatsapp-number");

  return (
    <Wrapper>
      <Box sx={{ maxWidth: 480, mx: "auto", py: 4, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <IconButton size="small" onClick={goToNumbers} sx={{ color: "#25D366" }} aria-label="Back">
            <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <WhatsAppIcon sx={{ color: "#25D366", fontSize: 28 }} />
          <Typography variant="h6">
            {telecomunication.whatsapp_title?.[langIndex] || "WhatsApp"}
          </Typography>
        </Box>

        {status === "loading" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 4 }}>
            <CircularProgress size={40} sx={{ color: "#25D366" }} />
            <Typography variant="body2" color="text.secondary">
              {telecomunication.connecting?.[langIndex] || "Connecting..."}
            </Typography>
          </Box>
        )}

        {status === "success" && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {telecomunication.whatsapp_connected_success?.[langIndex] ||
              "WhatsApp number connected successfully."}
          </Alert>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {telecomunication.whatsapp_connect_error?.[langIndex] || "Could not connect WhatsApp number."}
              {error && ` ${error}`}
            </Alert>
            <Button
              variant="contained"
              onClick={goToNumbers}
              sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#1da851" } }}
            >
              {telecomunication.whatsapp_manage_numbers?.[langIndex] || "WhatsApp numbers"}
            </Button>
          </>
        )}
      </Box>
    </Wrapper>
  );
}
